import { useQuery } from '@tanstack/react-query';

import type { GetAssignMotorcycleEngineNumberResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get assigned motorcycle engine number data from a purchased vehicle
 */
export const getAssignMotorcycleEngineNumber = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetAssignMotorcycleEngineNumberResponse> => {
	const response = await apiClient.get<GetAssignMotorcycleEngineNumberResponse>(
		ordersEndpoints.assignMotorcycleEngineNumber(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting assigned motorcycle engine number data from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetAssignMotorcycleEngineNumber = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.assignMotorcycleEngineNumber.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error(
					'Unable to get assigned motorcycle engine number without an order ID.',
				);
			}
			return getAssignMotorcycleEngineNumber(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
